// Global (dashboard-wide) settings popup — opened from the ⋯ topbar menu.
// Independent "default reasoning effort" selectors (Medium / High / xHigh / Max):
//   1. NEW samples — baked into each sample at creation; existing samples untouched.
//   2. Clients — LIVE default for your interactive sends to any client (no override).
//   3. Auto-approvals — LIVE default for auto-approved revisions (separate knob).
//   4. Exchanges — explicitly opened client dialogues, default High, separate API.
// A chat's composer chip overrides #2 for that chat; ⋯ → Auto-approve thinking
// overrides #3 for that client.

import { useEffect } from "react";
import { X, Zap, Users, RefreshCw } from "react-feather";
import { useSession } from "../store/session";
import { ExchangeDefaultSettings } from "./ExchangeSettings";
import { shownEffort } from "../lib/effortLevel";

type Level = "low" | "medium" | "high" | "xhigh" | "max";
/** Ultracode was retired on 9 Oct 2026: no selector offers it. A legacy stored "ultra"
 *  sample default shows (and is treated) as Max; picking Max saves a real "max". */
const LEVELS: { key: Level; label: string; blurb: string; hidden?: boolean }[] = [
  // "low" is accepted by the server but never offered; shown only when it is the stored value.
  { key: "low", label: "Low", blurb: "Lightest effort", hidden: true },
  { key: "medium", label: "Medium", blurb: "Balanced effort" },
  { key: "high", label: "High", blurb: "Standard effort" },
  { key: "xhigh", label: "xHigh", blurb: "More reasoning" },
  { key: "max", label: "Max", blurb: "Highest effort" },
];

export default function GlobalSettingsModal() {
  const close = useSession((s) => s.closeGlobalSettings);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  return (
    <div
      className="historyConfirm__backdrop"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-labelledby="gs-title"
    >
      <div className="historyConfirm__modal historyConfirm__modal--wide" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal__close" onClick={close} aria-label="Close">
          <X width={16} height={16} />
        </button>

        <h3 className="historyConfirm__title" id="gs-title">Global Settings</h3>
        <p className="modalSection__hint">Thinking defaults by task. Each run uses only its own setting—not every level below.</p>

        <EffortSection
          icon={<Zap width={14} height={14} />}
          title="New Samples"
          hint={<>For <strong>future sample builds</strong>. Existing samples keep their setting.</>}
          which="sample"
        />
        <EffortSection
          icon={<Users width={14} height={14} />}
          title="Your Messages"
          hint={<>For messages <strong>you type in Operations</strong>. Does not set automatic revision or Exchange effort.</>}
          which="client"
        />
        <EffortSection
          icon={<RefreshCw width={14} height={14} />}
          title="Automatic Revisions"
          hint={<>For <strong>auto-approved website changes</strong>. Quotes still need your approval.</>}
          which="autorev"
        />

        <ExchangeDefaultSettings />
        <p className="gsFootnote">A chat's own thinking setting takes priority. Change your messages in the composer, or automatic work in that client's ⋯ menu.</p>
      </div>
    </div>
  );
}

function EffortSection({
  icon,
  title,
  hint,
  which,
}: {
  icon: React.ReactNode;
  title: string;
  hint: React.ReactNode;
  which: "sample" | "client" | "autorev";
}) {
  const current = useSession((s) =>
    which === "sample"
      ? s.sampleDefaultEffort
      : which === "client"
        ? s.clientDefaultEffort
        : s.autoRevisionDefaultEffort,
  );
  const saveSample = useSession((s) => s.saveSampleDefaultEffort);
  const saveClient = useSession((s) => s.saveClientDefaultEffort);
  const saveAutorev = useSession((s) => s.saveAutoRevisionDefaultEffort);
  const save = which === "sample" ? saveSample : which === "client" ? saveClient : saveAutorev;
  const shown = shownEffort(current);

  return (
    <div className="modalSection">
      <div className="modalSection__header">
        {icon}
        <span>{title}</span>
      </div>
      <p className="modalSection__hint">{hint}</p>
      <div className="gsEffort" role="group" aria-label={title}>
        {LEVELS.filter((l) => !l.hidden || shown === l.key).map((l) => (
          <button
            key={l.key}
            type="button"
            className={`gsEffort__opt gsEffort__opt--${l.key} ${shown === l.key ? "is-active" : ""}`}
            onClick={() => {
              // Compare the STORED value: a legacy "ultra" shows as Max, and picking Max saves "max".
              if (current === l.key) return;
              void save(l.key);
            }}
            aria-pressed={shown === l.key}
          >
            <span className="gsEffort__label">{l.label}</span>
            <span className="gsEffort__blurb">{l.blurb}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
